import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';

import { Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { AppState } from 'src/app/state';
import { Language } from 'src/app/models/language';
import { Subsection } from 'src/app/models/subsection';
import { Bar } from 'src/app/models/bar';
import { templatesTinymceInitConfig } from 'src/app/shared/components/tinymce-editor/tinymce-editor-config';
import { isThereEmptyTranslation } from 'src/app/dashboard/studies/helpers/translation-helper';
import { setSelectedLanguage } from 'src/app/dashboard/state/templates/templates.actions';
import {
  selectSelectedLanguage,
  selectSupportedLanguages,
} from 'src/app/dashboard/state/templates/templates.selectors';
import { selectSelectedSubsection } from 'src/app/dashboard/state/subsections/subsections.selectors';
import { selectBars, selectSelectedBar } from 'src/app/dashboard/state/bars/bars.selectors';
import { openDashboardConfirmationDialog } from 'src/app/dashboard/state/dashboard.actions';
import { deleteBar, setSelectedBar } from 'src/app/dashboard/state/bars/bars.actions';

const BAR_TITLE = 'bar-title';
const BAR_TEXT = 'bar-text';

@Component({
  selector: 'dashboard-bar',
  templateUrl: './bar.component.html',
  styleUrls: ['./bar.component.scss'],
})
export class BarComponent implements OnInit, OnDestroy {
  barForm: FormGroup;

  selectedSubsection: Subsection;
  bars: Bar[];
  selectedBar: Bar;
  selectedBarIndex: number;

  isEditingBar: boolean;

  supportedLanguages: Language[];
  selectedLanguage: Language;

  BAR_TITLE = BAR_TITLE;
  BAR_TEXT = BAR_TEXT;

  tinymceInitConfig = templatesTinymceInitConfig;

  radioGroup: any;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private changeDetectorRef: ChangeDetectorRef,
    private toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    this.barForm = this.formBuilder.group({
      barTranslations: this.formBuilder.array([]),
      hasDetails: [false],
      detailsPage: [],
    });

    this.subscriptions.add(
      this.store
        .select(selectSupportedLanguages)
        .subscribe((supportedLanguages) => (this.supportedLanguages = supportedLanguages))
    );

    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((language) => {
        this.selectedLanguage = language;
        this.changeDetectorRef.detectChanges();
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedSubsection).subscribe((subsection) => (this.selectedSubsection = subsection))
    );

    this.subscriptions.add(this.store.select(selectBars).subscribe((bars) => (this.bars = bars)));

    this.subscriptions.add(
      this.store.select(selectSelectedBar).subscribe((bar) => {
        this.selectedBar = bar;
        this.isEditingBar = !!this.selectedBar;
        this.removeAllTranslationFields();
        if (this.supportedLanguages) {
          this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
        }
      })
    );
  }

  get barTranslations(): FormArray {
    return this.barForm.get('barTranslations') as FormArray;
  }

  get hasDetails(): AbstractControl {
    return this.barForm.get('hasDetails') as AbstractControl;
  }

  get detailsPage(): AbstractControl {
    return this.barForm.get('detailsPage');
  }

  removeAllTranslationFields(): void {
    this.barTranslations.clear();
  }

  createTranslationFields(languageCode): void {
    const filteredTranslations = this.selectedBar?.translations?.filter(
      (translation) => translation.language === languageCode
    );

    const barTitle = this.formBuilder.group({
      id: [filteredTranslations?.length ? filteredTranslations[0]?.id : 0],
      language: [languageCode],
      identifier: [BAR_TITLE],
      translation: [
        filteredTranslations?.length ? filteredTranslations[0]?.translation : '',
        [Validators.required, Validators.maxLength(30)],
      ],
    });
    const barText = this.formBuilder.group({
      id: [filteredTranslations?.length ? filteredTranslations[1]?.id : 0],
      language: [languageCode],
      identifier: [BAR_TEXT],
      translation: [
        filteredTranslations?.length && filteredTranslations[1] ? filteredTranslations[1]?.translation : '',
        [Validators.maxLength(330)],
      ],
    });

    this.barTranslations.push(barTitle);
    this.barTranslations.push(barText);
  }

  selectTemplateLanguage(event): void {
    const foundSelected = this.supportedLanguages.find((language) => language.name === event.tab.textLabel);
    this.store.dispatch(setSelectedLanguage({ selectedLanguage: foundSelected }));
  }

  getBarTitle(bar) {
    const filteredTranslations = bar?.translations?.filter(
      (translation) => translation?.identifier === BAR_TITLE && translation?.language === this.selectedLanguage.code
    );
    return filteredTranslations?.length ? filteredTranslations[0].translation : '';
  }

  saveBar() {
    this.barForm.markAllAsTouched();

    if (isThereEmptyTranslation(this.barTranslations.value, BAR_TITLE)) {
      this.toastrService.error(`Please populate Bar Title for all languages`);
    }

    if (this.barForm.invalid) {
      return;
    }

    let bar = {
      ...this.barForm.value,
      templateSubsectionId: this.selectedSubsection.id,
      translations: this.barTranslations.value,
    };

    delete bar.barTranslations;

    if (this.isEditingBar) {
      const detailsPage = {
        id: this.selectedBar?.detail?.id ? this.selectedBar?.detail?.id : null,
        translations: this.barForm.value.detailsPage?.detailTranslations,
      };

      bar = {
        ...bar,
        id: this.selectedBar.id,
        detail: this.hasDetails.value ? detailsPage : null,
      };

      delete bar.detailsPage;

      this.bars.splice(this.selectedBarIndex, 1, bar);
    } else {
      bar = {
        ...bar,
        detail: null,
      };

      delete bar.detailsPage;

      this.bars.push(bar);
    }
    this.resetBars();
  }

  setSelectedBar(bar, index) {
    this.selectedBarIndex = index;
    this.store.dispatch(setSelectedBar({ selectedBar: bar }));
  }

  cancelEditingBar() {
    this.resetBars();
  }

  resetBars() {
    this.store.dispatch(setSelectedBar({ selectedBar: null }));
    this.radioGroup = null;
    this.barForm.reset();
    this.removeAllTranslationFields();
    if (this.supportedLanguages) {
      this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
    }
  }

  removeBar(bar) {
    this.store.dispatch(
      openDashboardConfirmationDialog({
        dialogData: {
          title: 'Are you sure you want to delete this subsection bar?',
          onConfirm: deleteBar({ bar }),
        },
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
